import Link from 'next/link'
import Icon from 'common/Icon'
import styles from './index.module.scss'
import { NAVY } from 'constants/colors'
import classNames from 'classnames'
import { enhanceUrlWithMarketingParams } from 'lib/marketingParams'

type PracticePanelSectionProps = {
  containerClass?: string
  logoUrl: string
  logoAltText: string
  title: string
  buttonAriaLabel: string
  practicePanelLinkHref?: string
  practicePanelLinkTarget?: string
  handleLinkClick?: () => void
  /** Internal app routes should use Next.js client-side routing instead of a full page load. */
  isInternalLink?: boolean
}

/**
 * Practice panel row with a link that appends persisted marketing params to
 * eligible target URLs (relative paths and thriveworks.com domains).
 */
const PracticePanelSection: React.FC<PracticePanelSectionProps> = ({
  containerClass = '',
  logoUrl,
  logoAltText,
  title,
  buttonAriaLabel,
  practicePanelLinkHref = '',
  practicePanelLinkTarget = '_self',
  handleLinkClick,
  isInternalLink = false,
  children,
}) => {
  const href = enhanceUrlWithMarketingParams(practicePanelLinkHref)

  const onClick = () => {
    !!handleLinkClick && handleLinkClick()
  }

  const content = (
    <div className={styles.practice_panel_section_content}>
      {children}
      <span
        aria-hidden="true"
        className={`${styles.practice_panel_section_cta} ~bg-transparent ~border-0`}
      >
        <Icon icon="dateNextArrow" className="fa-sm" fill={NAVY} />
      </span>
    </div>
  )

  return (
    <div className={classNames('~flex', containerClass)}>
      <img width="24px" height="24px" src={logoUrl} alt={logoAltText} />
      <div className={styles.practice_panel_section_container}>
        <span>{title}</span>
        {isInternalLink ? (
          <Link
            href={href}
            target={practicePanelLinkTarget}
            aria-label={buttonAriaLabel}
            onClick={onClick}
          >
            {content}
          </Link>
        ) : (
          <a
            href={href}
            target={practicePanelLinkTarget}
            rel="noreferrer"
            aria-label={buttonAriaLabel}
            onClick={onClick}
          >
            {content}
          </a>
        )}
      </div>
    </div>
  )
}

export default PracticePanelSection
